import React, { useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { postsApi } from 'api';
import { usePanel } from 'contexts/panelContext';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { Post } from 'types/Dashboard';
import { InputText } from 'components/_new/InputText';
import { Button } from 'components/_new/Button';
import { DirectoryCards } from 'components/knowledgeBase/DirectoryCards';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { HorizontalActionList } from 'components/_new/HorizontalActionList/HorizontalActionList';
import { useKnowledgeBase } from 'components/knowledgeBase/knowledgeBaseContext';
import { Icon } from 'components/_new/Icon';
import { FavoriteIcon } from 'components/knowledgeBase/FavoriteIcon';
import { ToreadIcon } from 'components/knowledgeBase/ToreadIcon';
import { ReadedIcon } from 'components/knowledgeBase/ReadedIcon';
import { Link, useNavigate } from 'react-router-dom';
import { slugify } from 'utils/helpers';
import { KnowledgeBaseArticleContentItem } from 'types/KnowledgeBase';
import { ArticlePresenterDialog } from 'components/knowledgeBase/ArticlePresenterDialog';
import { Box, BoxSection } from 'components/_new/Box';
import { useKnowledgeBaseArticleLink } from 'components/knowledgeBase/KnowledgeBaseLink';
import { EmptyState } from 'utils/emptyState';
import ProgressSteps from '../_common/ProgressSteps/ProgressSteps';
import NotificationPostSkeleton from './NotificationPostSkeleton';
import NotificationPost from './NotificationPost';

const Wall = () => {
	const postsRef = useRef<HTMLDivElement>(null);

	const { currentProgramMembership, currentApplicationData } = usePanel();
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [modalArticleId, setModalArticleId] = useState<number | null>(null);

	const { control, handleSubmit } = useForm({ defaultValues: { search: '' } });
	const { knowledgeBaseLinkPrefix, context } = useKnowledgeBase();

	const [search, setSearch] = useState<string | null>(null);

	const { data: highlightPost } = useQuery(
		[
			'HIGHLIGHT_POST',
			{
				membershipId: currentProgramMembership?.id,
				applicationId: currentApplicationData.id,
			},
		],
		() =>
			postsApi.getLastHighlightedPost({
				membershipId: currentProgramMembership?.id,
				applicationId: currentApplicationData.id,
			}),
		{
			enabled: true,
		}
	);

	const { data: homepageRcomendedData } = useQuery(
		['KNOWLEDGE_BASE_HOMEPAGE', { applicationId: currentApplicationData.id }],
		() => knowledgeBaseApi.application.getHomepageRecommendedArticles(currentApplicationData.id),
		{ enabled: true }
	);

	const postsPerLoad = 10;
	const { refetch, data, fetchNextPage, isLoading, isFetching, isFetchingNextPage, isSuccess, hasNextPage } =
		useInfiniteQuery({
			queryKey: [
				'posts',
				{
					membershipId: currentProgramMembership?.id,
					applicationId: currentApplicationData.id,
					search,
				},
			],
			queryFn: ({ pageParam = 1 }) =>
				postsApi.getPosts({
					membershipId: currentProgramMembership?.id,
					applicationId: currentApplicationData.id,
					params: {
						page: pageParam,
						pageSize: postsPerLoad,
						search,
					},
				}),
			enabled: true,
			getNextPageParam: (lastPage) => {
				const nextPage = lastPage.pagination.currentPage + 1;
				if (nextPage > lastPage.pagination.itemsPerPage) {
					return undefined;
				}
				return nextPage;
			},
		});

	const posts = (data?.pages || []).reduce<Post[]>((current, item) => {
		return [...current, ...item.data];
	}, []);
	const handleSubmitForm = handleSubmit(({ search: inputSearch }) => {
		if (inputSearch === search) {
			refetch();
		} else {
			setSearch(inputSearch && inputSearch.length > 0 ? inputSearch : null);
		}
	});

	const [searchMode, setSearchMode] = useState(false);
	const noPosts = !isLoading && isSuccess && posts && posts.length === 0;

	const openArticle = (article: KnowledgeBaseArticleContentItem) => {
		const { articleId, slug } = article;
		const pathname = `${knowledgeBaseLinkPrefix}/article/${articleId}${slug ? `-${slugify(slug)}` : ''}`;
		switch (context) {
			case 'coordinator':
			case 'program-admin':
				navigate({
					pathname,
				});
				break;
			case 'user-panel':
			default:
				setModalArticleId(articleId);
				break;
		}
	};

	const { link } = useKnowledgeBaseArticleLink('faq');
	const faqLink = useMemo(() => {
		return link?.to.pathname;
	}, [link]);

	return (
		<div className="h-full flex flex-column gap-4">
			<ProgressSteps />

			{highlightPost && (
				<div>
					<SubPageTitle title={t('userPanel.dashboard.highlightNotification')} />
					<div className="flex flex-column my-3 gap-1">
						<NotificationPost
							postData={highlightPost}
							withComments
							getPostCommentsRequest={({ postId, pageParam }) => {
								return postsApi.getPostComments({
									postId,
									params: {
										page: pageParam[0] || 1,
										limit: 3,
									},
								});
							}}
							addPostCommentRequest={({ postId, payload }) => {
								return postsApi.addPostComment({ postId, payload });
							}}
						/>
						<div className="flex flex-row justify-content-end">
							<Button
								label={t('userPanel.dashboard.moreNotifications')}
								variant="primary-text"
								onClick={() => {
									postsRef?.current?.scrollIntoView({
										behavior: 'smooth',
										block: 'center',
										inline: 'nearest',
									});
								}}
							/>
						</div>
					</div>
				</div>
			)}

			{homepageRcomendedData && homepageRcomendedData.length > 0 && (
				<div className="flex flex-column gap-1">
					<SubPageTitle title={t('knowledgeBase.homepage.recommendedArticles')} />
					<ArticlePresenterDialog
						articleId={modalArticleId}
						visible={modalArticleId !== null}
						onHide={() => setModalArticleId(null)}
						onFlagChange={() => refetch()}
						knowledgeBaseLinkPrefix={knowledgeBaseLinkPrefix}
					/>
					<HorizontalActionList
						itemsClassName="flex flex-row gap-2 justify-content-start align-content-stretch"
						maxSwipeWidth={150}
					>
						{homepageRcomendedData.map((item) => {
							const { id, articleId, title, slug, info } = item;
							const pathname = `${knowledgeBaseLinkPrefix}/article/${articleId}${
								slug ? `-${slugify(slug)}` : ''
							}`;
							return (
								<Link
									to={{ pathname }}
									key={id}
									className="flex article-tile"
									onClick={(event) => {
										event.preventDefault();
										openArticle(item);
									}}
									tabIndex={-1}
								>
									<Box
										hoverable
										variant="white-bordered"
										className="w-full"
										img={info?.imageUrl || 'default'}
									>
										<BoxSection horizontal contentClassName="justify-content-between">
											<div className="inline-flex gap-2 align-items-center">
												{context === 'user-panel' && (
													<>
														<FavoriteIcon checked={info.flag.favorite} />
														<ToreadIcon checked={info.flag.toRead} />
														<ReadedIcon checked={info.flag.readed} />
													</>
												)}
												{['program-admin', 'coordinator'].includes(context) && (
													<>
														{info?.access?.mentor && (
															<span
																title={t(
																	'knowledgeBase.programPanel.article.mentorAccess'
																)}
															>
																<Icon
																	name="user-tie"
																	className="text-purple-500 text-lg"
																/>
															</span>
														)}
														{info?.access?.mentee && (
															<span
																title={t(
																	'knowledgeBase.programPanel.article.menteeAccess'
																)}
															>
																<Icon
																	name="user-graduate"
																	className="text-blue-500 text-lg"
																/>
															</span>
														)}
													</>
												)}
											</div>
											<div className="inline-flex gap-1 align-items-center text-muted text-lg text-nowrap">
												<Icon name="clock" iconStyle="regular" />
												{info.timeToRead || 15} m
											</div>
										</BoxSection>

										<BoxSection className="flex-1">
											<span className="strong">{title}</span>
										</BoxSection>

										<BoxSection>
											<Button
												label={t('actions.open')}
												variant="gray-primary-solid"
												block
												onClick={(event) => {
													event.stopPropagation();
													event.preventDefault();
													openArticle(item);
												}}
											/>
										</BoxSection>
									</Box>
								</Link>
							);
						})}
					</HorizontalActionList>
				</div>
			)}

			<div className="flex flex-column gap-1">
				<SubPageTitle title={t('userPanel.dashboard.moreFromKnowledgeBase')} />
				<DirectoryCards
					homepageMode
					additionalCards={[
						{
							key: 'faq',
							name: 'FAQ',
							link: faqLink!,
							icon: 4,
						},
						{
							key: 'showall',
							name: t('knowledgeBase.fullKnowledgeBase'),
							link: { pathname: knowledgeBaseLinkPrefix },
						},
					]}
					renderRight={
						<span
							className="directory-arrow inline-flex align-items-center justify-content-center bg-new-gray border-circle p-1"
							style={{ lineHeight: 0 }}
						>
							<Icon name="arrow-right" className="inline-block w-1rem h-1rem" />
						</span>
					}
				/>
			</div>

			<div>
				<SubPageTitle
					ref={postsRef}
					id="posts"
					title={t('userPanel.dashboard.notifications')}
					right={() =>
						searchMode ? (
							<form onSubmit={handleSubmitForm} className="flex flex-row justify-content-end gap-2">
								<Controller
									control={control}
									name="search"
									render={({ field }) => (
										<InputText
											inline
											search
											{...field}
											onChange={(event) => {
												if (event.currentTarget.value === '') {
													setSearchMode(false);
													setSearch(null);
													refetch();
												}
												field?.onChange(event);
											}}
										/>
									)}
								/>
								<Button submit label={t('programPanel.post.searchPosts')} loading={isFetching} />
							</form>
						) : (
							<Button
								label={t('actions.search')}
								onClick={() => setSearchMode((prev) => !prev)}
								icon="search"
								iconOnly
								variant="primary-text"
								variantSize="lg"
							/>
						)
					}
				/>
				{isLoading && (
					<>
						<div className="my-3">
							<NotificationPostSkeleton />
						</div>
						<div className="my-3">
							<NotificationPostSkeleton />
						</div>
						<div className="my-3">
							<NotificationPostSkeleton />
						</div>
					</>
				)}
				{noPosts && (
					<EmptyState
						title={search ? t('programPanel.post.noPostsSearch') : t('programPanel.post.noPosts')}
					/>
				)}
				{isSuccess &&
					posts &&
					posts.length > 0 &&
					posts.map((post) => (
						<div key={post.id} className="my-3">
							<NotificationPost
								postData={post}
								withComments
								getPostCommentsRequest={({ postId, pageParam }) => {
									return postsApi.getPostComments({
										postId,
										params: {
											page: pageParam[0] || 1,
											limit: 3,
										},
									});
								}}
								addPostCommentRequest={({ postId, payload }) => {
									return postsApi.addPostComment({ postId, payload });
								}}
							/>
						</div>
					))}
				{!noPosts && hasNextPage && (
					<div className="flex justify-content-center pb-3">
						<Button
							label={t('misc.loadMore')}
							onClick={() => fetchNextPage()}
							loading={isFetchingNextPage}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default Wall;
